import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { usePersonas } from '@/hooks/usePersonas';
import { isNavItemActive, type NavItem } from './navItems';
import { segmentDotClass } from './segmentColor';

interface CommandPaletteProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly items: readonly NavItem[];
  readonly pathname: string;
}

/**
 * What the sidebar's search field opens.
 *
 * It searches what the app actually holds: the pages the account can see, and
 * the saved audiences. Nothing here reaches for a search endpoint, because
 * there is none.
 */
export const CommandPalette: React.FC<CommandPaletteProps> = ({
  open,
  onOpenChange,
  items,
  pathname,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { personas } = usePersonas({ type: 'funnel', enabled: open });

  const go = useCallback(
    (to: string) => {
      onOpenChange(false);
      navigate(to);
    },
    [navigate, onOpenChange]
  );

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder={t('app.v11.shell.palette.placeholder')} />
      <CommandList>
        <CommandEmpty>{t('app.v11.shell.palette.empty')}</CommandEmpty>

        <CommandGroup heading={t('app.v11.shell.sections.workspace')}>
          {items.map(item => {
            const Icon = item.icon;
            return (
              <CommandItem
                key={item.path}
                value={t(item.label)}
                onSelect={() => go(item.path)}
                disabled={isNavItemActive(pathname, item.path)}
              >
                <Icon className="mr-2 h-4 w-4" />
                {t(item.label)}
              </CommandItem>
            );
          })}
        </CommandGroup>

        {personas.length > 0 && (
          <CommandGroup heading={t('app.v11.shell.sections.recentSegments')}>
            {personas.map(persona => (
              <CommandItem
                key={persona.id}
                value={persona.name}
                onSelect={() => go(`/v11/funnel?persona=${persona.id}`)}
              >
                <span
                  aria-hidden="true"
                  className={`mr-2 h-2 w-2 rounded-full ${segmentDotClass(persona.id)}`}
                />
                {persona.name}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
};
